import { useState, useEffect } from 'react';
import {
  X, Bell, ChevronRight, Loader2, Settings, Eye, EyeOff, CheckCheck, Trash2, Inbox,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Separator } from '../ui/separator';
import { projectId } from '../../utils/supabase/info';
import { MessageTypeBadge, SenderRoleBadge } from './MessageTypeBadge';
import type { EngagementNotification } from './types';

interface EngagementNotificationPanelProps {
  userId: string;
  userRole: string;
  accessToken: string;
  onClose: () => void;
  onOpenThread?: (engagementId: string, threadId: string) => void;
  onOpenFocusMode?: () => void;
  /** Opens the user's other notifications (mentions, assignments, digests); firm users only. */
  onOpenUpdates?: () => void;
  /** Unread count of those other notifications, shown on the Updates button. */
  updatesUnread?: number;
}

export function EngagementNotificationPanel({
  userId,
  userRole,
  accessToken,
  onClose,
  onOpenThread,
  onOpenFocusMode,
  onOpenUpdates,
  updatesUnread = 0,
}: EngagementNotificationPanelProps) {
  const [notifications, setNotifications] = useState<EngagementNotification[]>([]);
  const [grouped, setGrouped] = useState<Record<string, EngagementNotification[]>>({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [focusModeActive, setFocusModeActive] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const baseUrl = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd`;

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${baseUrl}/engagement-messaging/notifications?userId=${userId}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
        setGrouped(data.grouped || {});
        setUnreadCount(data.unreadCount || 0);
        setFocusModeActive(data.focusModeActive || false);
      }
    } catch (error) {
      console.error('Error loading engagement notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notifId: string) => {
    try {
      await fetch(
        `${baseUrl}/engagement-messaging/notifications/${notifId}/read?userId=${userId}`,
        { method: 'PUT', headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setNotifications((prev) =>
        prev.map((n) => (n.id === notifId ? { ...n, read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await fetch(
        `${baseUrl}/engagement-messaging/notifications/mark-all-read`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ userId }),
        }
      );
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Delete one (RESTORE_PLAN R2.5: the old NotificationCenter's delete)
  const deleteNotification = async (notif: EngagementNotification) => {
    try {
      const response = await fetch(
        `${baseUrl}/engagement-messaging/notifications/${encodeURIComponent(notif.id)}?userId=${userId}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (response.ok || response.status === 404) {
        setNotifications((prev) => prev.filter((n) => n.id !== notif.id));
        if (!notif.read) setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleNotificationClick = (notif: EngagementNotification) => {
    if (!notif.read) {
      markAsRead(notif.id);
    }
    if (onOpenThread && notif.deepLink) {
      onOpenThread(notif.deepLink.engagementId, notif.deepLink.threadId);
      onClose();
    }
  };

  const formatTime = (ts: string) => {
    const d = new Date(ts);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  };

  const filteredNotifications =
    filter === 'unread'
      ? notifications.filter((n) => !n.read)
      : notifications;

  // Group filtered notifications by engagement
  const filteredGrouped: Record<string, EngagementNotification[]> = {};
  for (const notif of filteredNotifications) {
    const key = notif.engagementId || 'unknown';
    if (!filteredGrouped[key]) filteredGrouped[key] = [];
    filteredGrouped[key].push(notif);
  }

  return (
    <div className="fixed inset-0 bg-black/25 z-50" onClick={onClose}>
      <div
        className="absolute top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-y-2 px-5 py-4 border-b shrink-0">
          <div className="flex items-center gap-2.5">
            <Bell className="h-5 w-5 text-foreground" />
            <h2 className="text-base font-semibold">Notifications</h2>
            {unreadCount > 0 && (
              <Badge variant="destructive" className="text-xs px-1.5 py-0">
                {unreadCount}
              </Badge>
            )}
            {focusModeActive && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-violet-50 text-violet-700 border-violet-200">
                Focus Mode
              </Badge>
            )}
          </div>
          <div className="ml-auto flex items-center gap-1">
            {onOpenUpdates && (
              <Button variant="ghost" size="sm" onClick={onOpenUpdates} className="text-xs h-7" title="Mentions, assignments and other updates">
                <Inbox className="h-3.5 w-3.5 mr-1" />
                Updates
                {updatesUnread > 0 && (
                  <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 py-0">{updatesUnread}</Badge>
                )}
              </Button>
            )}
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" onClick={markAllAsRead} className="text-xs h-7">
                <CheckCheck className="h-3.5 w-3.5 mr-1" />
                Mark all read
              </Button>
            )}
            {onOpenFocusMode && (
              <Button variant="ghost" size="icon" onClick={onOpenFocusMode} className="h-8 w-8">
                <Settings className="h-4 w-4" />
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs
          value={filter}
          onValueChange={(v) => setFilter(v as 'all' | 'unread')}
          className="flex flex-col flex-1 min-h-0"
        >
          <div className="px-5 pt-3 shrink-0">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="all" className="text-xs">
                All ({notifications.length})
              </TabsTrigger>
              <TabsTrigger value="unread" className="text-xs">
                Unread ({unreadCount})
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value={filter} className="flex-1 min-h-0 mt-0 pt-3">
            <ScrollArea className="h-full">
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : filteredNotifications.length === 0 ? (
                <div className="py-16 text-center px-6">
                  <Bell className="h-10 w-10 mx-auto text-muted-foreground/20 mb-3" />
                  <p className="text-sm text-muted-foreground font-medium">
                    {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Engagement messages will appear here
                  </p>
                </div>
              ) : (
                <div className="pb-6">
                  {Object.entries(filteredGrouped).map(([engId, notifs]) => {
                    const engName = notifs[0]?.engagementName || 'Unknown Engagement';
                    const engUnread = notifs.filter((n) => !n.read).length;

                    return (
                      <div key={engId}>
                        {/* Engagement Group Header */}
                        <div className="sticky top-0 bg-slate-50 px-5 py-2 flex items-center justify-between z-10">
                          <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
                            {engName}
                          </span>
                          {engUnread > 0 && (
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                              {engUnread} new
                            </Badge>
                          )}
                        </div>

                        {/* Notification Items */}
                        {notifs.map((notif) => (
                          <div
                            key={notif.id}
                            data-testid="engagement-notification"
                            className={`flex items-start border-b border-slate-100 ${!notif.read ? 'bg-blue-50/30' : ''}`}
                          >
                          <button
                            onClick={() => handleNotificationClick(notif)}
                            className="flex-1 min-w-0 text-left pl-5 pr-2 py-3 hover:bg-slate-50/80 transition-colors"
                          >
                            <div className="flex items-start gap-3">
                              {/* Unread dot */}
                              <div className="mt-1.5 shrink-0">
                                {!notif.read ? (
                                  <div className="h-2 w-2 rounded-full bg-blue-500" />
                                ) : (
                                  <div className="h-2 w-2" />
                                )}
                              </div>

                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs font-semibold text-foreground">
                                    {notif.senderName}
                                  </span>
                                  <SenderRoleBadge roleLabel={notif.senderRoleLabel} size="sm" />
                                  <MessageTypeBadge classification={notif.classification} size="sm" />
                                </div>

                                <p className="text-xs font-medium text-foreground/80 mb-0.5">
                                  {notif.threadSubject}
                                </p>

                                {notif.configItemName && (
                                  <p className="text-[10px] text-muted-foreground mb-0.5">
                                    {notif.configItemName}
                                  </p>
                                )}

                                <p className="text-xs text-muted-foreground line-clamp-2">
                                  {notif.preview}
                                </p>

                                <span className="text-[10px] text-muted-foreground mt-1 block">
                                  {formatTime(notif.createdAt)}
                                </span>
                              </div>

                              <ChevronRight className="h-4 w-4 text-muted-foreground/40 shrink-0 mt-1" />
                            </div>
                          </button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="mt-2 mr-3 h-7 w-7 shrink-0 text-muted-foreground hover:text-red-600"
                            aria-label={`Delete notification: ${notif.threadSubject || notif.preview || 'notification'}`}
                            title="Delete"
                            onClick={() => void deleteNotification(notif)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
